import {
  Box,
  Typography, styled, useMediaQuery, useTheme,
} from '@mui/material';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { VisualizerDetailsPeriodData } from '../../models';
import { ChartService, VisualizerService } from '../../services';
import Label from './Label';

const VisualizerDetailsChartContainer = styled(Box)(({ theme }) => ({
  borderTop: '1px solid lightgray',
  paddingTop: '16px',
  paddingBottom: '24px',
}));

const VisualizerDetailsTable = styled(Box)(({ theme }) => ({
  display: 'grid',
  textAlign: 'center',
  rowGap: '15px',
  gridTemplateColumns: 'repeat(24, 29.6px)',
  gridTemplateRows: 'repeat(1, 56px)',
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: 'repeat(12, 27.6px)',
    gridTemplateRows: 'repeat(2, 52px)',
  },

  '& div:first-of-type .chart-cell': {
    borderTopLeftRadius: '10px',
    borderBottomLeftRadius: '10px',
  },

  '& div:last-of-type .chart-cell': {
    borderTopRightRadius: '10px',
    borderBottomRightRadius: '10px',
  },
}));

const VisualizerDetailsTableCell = styled(Box)(({ theme }) => ({
  borderRight: '2px solid rgba(0,0,0, 0)',
  position: 'relative',
  '.chart-cell': {
    height: '28px',
    fontSize: '12px',
    lineHeight: '28px',
    [theme.breakpoints.down('md')]: {
      height: '26px',
      lineHeight: '26px',
    },
  },
}));

type Props = {
  detailsMonthPeriod: VisualizerDetailsPeriodData;
}

function VisualizerDetailsChart({ detailsMonthPeriod }: Props) {
  const theme = useTheme();
  const mappedTicks = useMemo(() => VisualizerService.mapTicks(detailsMonthPeriod), [detailsMonthPeriod]);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <VisualizerDetailsChartContainer data-testid="visualizer-details-period-chart">
      <Typography variant="subtitle2" sx={{ fontWeight: 'Bold', color: '#00000099' }}>
        <FormattedMessage id={`visualizer-details.label-${detailsMonthPeriod.label}`} />
      </Typography>
      <VisualizerDetailsTable
        aria-label={`visualizerDetailsChart${detailsMonthPeriod.label.charAt(0).toUpperCase() + detailsMonthPeriod.label.slice(1)}`}
      >
        {detailsMonthPeriod.hours.map(([peak, amount], index) => {
          const color = ChartService.getMappedColor(peak);

          const isFirstHour = index === 0;
          const isLastHour = index === detailsMonthPeriod.hours.length - 1;
          const isMidScreenLabel = isSmallScreen && index === 12;
          const isSmallScreenLabel = isSmallScreen && index === 11;
          const showLeftLabel = (isFirstHour && mappedTicks[index]) || isMidScreenLabel;
          const showRightLabel = isLastHour || isSmallScreenLabel;

          return (
            <VisualizerDetailsTableCell key={`${peak}-${+index}`}>
              <div
                className="chart-cell"
                style={{ backgroundColor: color, color: peak === 'regular' ? 'white' : 'black' }}
              >
                {amount.toFixed()}
              </div>
              {showLeftLabel && <Label align="left" flush>{mappedTicks[index]}</Label>}
              {!showLeftLabel && <Label align="left">{mappedTicks[index]}</Label>}
              {showRightLabel && <Label align="right" flush>{mappedTicks[index + 1]}</Label>}
            </VisualizerDetailsTableCell>
          );
        })}
      </VisualizerDetailsTable>
    </VisualizerDetailsChartContainer>
  );
}

export default VisualizerDetailsChart;
